/* ============================================================
   VEYRAN custom motion v1.0 — independent file, safe to remove
   - Scroll reveal via IntersectionObserver (no dependency)
   - Hero entrance + parallax (desktop only, rAF-throttled)
   - Brand Film: GSAP ScrollTrigger if available, IO fallback
   All wrapped defensively: any failure must not break cart/checkout.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.matchMedia('(max-width: 768px)').matches;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ---------- 1. Scroll reveal ---------- */
  function initReveal() {
    try {
      var els = document.querySelectorAll(
        'main .shopify-section:not(:first-child), .product-list, .collection-list, .media-with-content, footer'
      );
      if (!els.length || reduceMotion || !('IntersectionObserver' in window)) return;
      els.forEach(function (el, i) {
        el.classList.add('vy-reveal');
        el.setAttribute('data-vy-delay', String(i % 4));
      });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('vy-in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      els.forEach(function (el) { io.observe(el); });
    } catch (err) { /* no-op: never break the page */ }
  }

  /* ---------- 2. Hero entrance + parallax ---------- */
  function initHero() {
    try {
      var hero = document.querySelector('.hero');
      if (!hero) return;
      if (!reduceMotion) hero.classList.add('vy-hero-anim');

      if (reduceMotion || isMobile) return;
      var media = hero.querySelector('.hero__media');
      if (!media) return;
      hero.classList.add('vy-parallax');

      var ticking = false;
      function update() {
        ticking = false;
        var r = hero.getBoundingClientRect();
        // only while hero is in view
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var progress = Math.min(Math.max(-r.top / r.height, 0), 1);
        var y = progress * 90; // px of parallax travel
        var img = media.querySelector('img, video');
        if (img) img.style.transform = 'scale(1.12) translateY(' + y.toFixed(1) + 'px)';
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    } catch (err) { /* no-op */ }
  }

  /* ---------- 3. Quick-view affordance on product cards ---------- */
  function initQuickView() {
    try {
      if (isMobile) return; // hover-only affordance
      document.querySelectorAll('.product-card').forEach(function (card) {
        if (card.querySelector('.vy-quickview')) return;
        var link = card.querySelector('a.product-card__link, a[href*="/products/"]');
        if (!link) return;
        var btn = document.createElement('span');
        btn.className = 'vy-quickview';
        btn.textContent = 'Quick view';
        btn.setAttribute('aria-hidden', 'true');
        card.style.position = 'relative';
        card.appendChild(btn);
        // make the pill clickable -> follow card link
        btn.style.pointerEvents = 'auto';
        btn.style.cursor = 'pointer';
        btn.addEventListener('click', function () { link.click(); });
      });
    } catch (err) { /* no-op */ }
  }

  /* ---------- 4. Brand Film: pinned scroll narrative ---------- */
  function initBrandFilm() {
    try {
      var film = document.querySelector('.vy-film');
      if (!film || reduceMotion) return;
      var scenes = Array.prototype.slice.call(film.querySelectorAll('.vy-film__scene'));
      var dots = Array.prototype.slice.call(film.querySelectorAll('.vy-film__dots button'));
      if (scenes.length < 2) return;

      function activate(i) {
        scenes.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      }
      dots.forEach(function (d, k) {
        d.addEventListener('click', function () {
          var spacer = film.nextElementSibling;
          if (spacer && spacer.classList.contains('vy-film__spacer')) {
            var y = film.offsetTop + (spacer.offsetHeight / scenes.length) * k;
            window.scrollTo({ top: y, behavior: 'smooth' });
          } else { activate(k); }
        });
      });

      // GSAP path (cinematic pin + scrub)
      if (window.gsap && window.ScrollTrigger && !isMobile) {
        gsap.registerPlugin(ScrollTrigger);
        var tl = gsap.timeline({
          scrollTrigger: {
            trigger: film,
            start: 'top top',
            end: '+=' + (scenes.length * 90) + '%',
            pin: true,
            scrub: 0.6,
            onUpdate: function (self) {
              var i = Math.min(scenes.length - 1, Math.floor(self.progress * scenes.length));
              activate(i);
            }
          }
        });
        // keep timeline alive for scrub; scenes crossfade via CSS class
        tl.to({}, { duration: 1 });
        return;
      }

      // Fallback: IO-driven scene switching (works everywhere)
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) activate(parseInt(e.target.getAttribute('data-scene'), 10) || 0);
          });
        }, { threshold: 0.55 });
        scenes.forEach(function (s) { io.observe(s); });
      }
      activate(0);
    } catch (err) { /* no-op */ }
  }

  /* ---------- 5. Force inline autoplay for background videos ----------
     iOS Safari blocks autoplay unless the video is muted AND playsinline.
     Re-assert both attributes and retry play() so hero / brand videos
     start on mobile instead of showing a play button.
     Note: iOS Low Power Mode disables all autoplay — no code can override
     that; it must be turned off on the device. */
  function initVideoAutoplay() {
    try {
      var videos = document.querySelectorAll('.hero video, .veyran-video video');
      if (!videos.length) return;
      function arm(video) {
        try {
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          var p = video.play();
          if (p && p.catch) p.catch(function () { /* retry on gesture */ });
        } catch (err) { /* no-op */ }
      }
      videos.forEach(function (video) {
        if (video.readyState >= 2) { arm(video); return; }
        video.addEventListener('canplay', function () { arm(video); }, { once: true });
        var gesture = function () { arm(video); };
        document.addEventListener('touchstart', gesture, { once: true, passive: true });
        document.addEventListener('click', gesture, { once: true });
      });
    } catch (err) { /* no-op */ }
  }

  onReady(function () {
    initReveal();
    initHero();
    initQuickView();
    initBrandFilm();
    initVideoAutoplay();
  });
})();
